import { useQuery } from '@tanstack/react-query'
import { startOfWeek, addDays, formatISO, isSameDay, format } from 'date-fns'
import { api, type Article, type ArticleStatus } from '@/lib/api'
import { cn } from '@/lib/utils'

const STATUS_CLASS: Record<ArticleStatus, string> = {
  draft:     'bg-status-draft-bg border-l-status-draft-accent text-status-draft-fg',
  in_review: 'bg-status-review-bg border-l-status-review-accent text-status-review-fg',
  approved:  'bg-status-approved-bg border-l-status-approved-accent text-status-approved-fg',
  published: 'bg-status-published-bg border-l-status-published-accent text-status-published-fg',
  archived:  'bg-surface-muted border-l-ink-tertiary text-ink-secondary',
}

function DayColumn({ date, articles, isToday }: { date: Date; articles: Article[]; isToday: boolean }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <div className={cn('text-xs px-0.5', isToday ? 'text-brand-600 font-medium' : 'text-ink-tertiary')}>
        {format(date, 'EEE d')}
      </div>
      {articles.map((a) => (
        <div
          key={a.id}
          className={cn(
            'rounded border-l-2 px-2 py-1.5 text-[11px] leading-tight cursor-pointer hover:opacity-90',
            STATUS_CLASS[a.status]
          )}
          title={a.title}
        >
          <div className="font-medium truncate">{a.title}</div>
          <div className="opacity-80 mt-0.5">
            {a.writer_id ? writerInitials(a.writer_id) : 'Agent'} · {a.word_count ? `${(a.word_count/1000).toFixed(1)}k` : a.type}
          </div>
        </div>
      ))}
    </div>
  )
}

function writerInitials(id: string) {
  // Quick display helper — the API also returns full names; this is a fallback for the calendar cards.
  const map: Record<string, string> = {
    wtr_ben:    'Ben',
    wtr_will:   'Will',
    wtr_jenna:  'Jenna',
    wtr_marcus: 'Marcus',
  }
  return map[id] ?? id
}

export function EditorialCalendar() {
  // Anchor on current week Mon–Fri
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
  const days = [0, 1, 2, 3, 4].map((i) => addDays(weekStart, i))
  const from = formatISO(days[0])
  const to = formatISO(addDays(days[4], 1))

  const { data: articles = [] } = useQuery({
    queryKey: ['articles', { from, to }],
    queryFn: () => api.articles({ from, to }),
  })

  return (
    <section className="border border-border rounded-md p-3 min-w-0">
      <div className="flex justify-between items-center mb-2.5">
        <h2 className="text-sm font-medium">Editorial calendar</h2>
        <span className="text-xs text-ink-tertiary">Week of {format(weekStart, 'MMM d')}</span>
      </div>
      <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
        <div className="grid md:grid-cols-5 grid-flow-col md:grid-flow-row auto-cols-[140px] md:auto-cols-auto gap-1.5">
          {days.map((d) => {
            const dayArticles = articles.filter((a) =>
              a.scheduled_for && isSameDay(new Date(a.scheduled_for), d)
            )
            return <DayColumn key={d.toISOString()} date={d} articles={dayArticles} isToday={isSameDay(d, new Date())} />
          })}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 mt-3 text-xs text-ink-secondary">
        {([
          ['Draft','#7F77DD'], ['In review','#EF9F27'], ['Approved','#1D9E75'], ['Published','#888780']
        ] as const).map(([label, color]) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
    </section>
  )
}
